'use client';

import Close from '@hc-frontend/core-icons/kit/close';
import {
  Placement,
  ProductType,
  REDIRECT_TYPE_PARAM,
  RedirectType,
} from '@hc-frontend/core-types';
import { useEventBus, useTrackingContexts } from '@hc-frontend/core-ui';
import { ComponentImpressionEvent } from '@hc-frontend/features-tracking';
import { ButtonWithTracking } from '@hc-frontend/shells-shared';
import { useRouter } from 'next/navigation';
import { MouseEventHandler, useEffect, useRef, useState } from 'react';

import { Countdown } from '../../atoms/countdown/countdown';
import { useSurveyInactivityModal } from './provider';

type SurveyInactivityModalProps = {
  id: string;
  placement: Placement;
  product: ProductType;
  redirectTo: string;
  redirectType: RedirectType;
  redirectTimeoutSeconds: number;
};

export function SurveyInactivityModal({
  id,
  placement,
  product,
  redirectTo,
  redirectTimeoutSeconds,
}: SurveyInactivityModalProps) {
  const ref = useRef<HTMLDialogElement | null>(null);
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { isOpen, setIsOpen } = useSurveyInactivityModal();
  const router = useRouter();

  const handleRedirect = (redirectType: RedirectType) => {
    setIsRedirecting(true);
    const params = new URLSearchParams({
      [REDIRECT_TYPE_PARAM]: redirectType,
    });
    router.push(`${redirectTo}?${params.toString()}`);
  };

  const handleBackdropClick: MouseEventHandler<HTMLDialogElement> = (event) => {
    if (ref.current && event.target === ref.current) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const open = () => {
      if (ref.current) {
        ref.current.showModal();
        eventBus.publish<ComponentImpressionEvent>({
          id,
          topic: 'core.ui.component.impression',
          source: ref.current,
          componentImpression: {
            component: id,
            action: 'display',
            category: placement,
            product,
          },
          contexts,
        });
      }
    };

    const close = () => {
      if (ref.current) {
        ref.current.close();
        eventBus.publish<ComponentImpressionEvent>({
          id,
          topic: 'core.ui.component.impression',
          source: ref.current,
          componentImpression: {
            component: id,
            action: 'hide',
            category: placement,
            product,
          },
          contexts,
        });
      }
    };

    if (isOpen) {
      open();
    } else {
      close();
    }
  }, [isOpen, eventBus, id, placement, product, contexts]);

  return (
    <dialog
      id={id}
      ref={ref}
      aria-modal="true"
      data-testid="dialog"
      onClick={handleBackdropClick}
      className={`max-w-[500px] rounded-lg bg-gray-0 shadow-lg backdrop:bg-[rgba(0,0,0,0.65)] ${isRedirecting ? 'pointer-events-none' : ''}`}
    >
      <button
        data-testid="close-button"
        className="absolute right-16-16 top-16-16 outline-none"
        onClick={() => setIsOpen(false)}
      >
        <Close className="size-16-16 text-gray-200" />
      </button>

      <div
        className={`w-full p-24-32 text-center ${isRedirecting ? 'animate-pulse' : ''}`}
      >
        <h1 className="hc-typography--heading-three mt-16-16 mb-12-12 text-balance">
          Are you still there?
        </h1>
        <p className="mb-32-32" data-testid="subtitle">
          <span className="font-bold">Great News!</span> — We found results for
          you while you were away. We&apos;ll take you to see results in{' '}
          <span className="font-bold">
            <Countdown
              isActive={isOpen && !isRedirecting}
              initialSeconds={redirectTimeoutSeconds}
              onEnd={() => handleRedirect(RedirectType.Enum.SURVEY_INACTIVITY)}
            />
          </span>{' '}
          seconds.
        </p>
        <div className="flex gap-4-8 flex-wrap items-stretch justify-center">
          <ButtonWithTracking
            id="survey-inactivity-modal-see-results"
            type="button"
            variant="primary-card"
            className="flex-1 justify-center text-center whitespace-nowrap"
            onClick={() => handleRedirect(RedirectType.Enum.DEFAULT)}
          >
            <span>See Results</span>
          </ButtonWithTracking>

          <ButtonWithTracking
            id="survey-inactivity-modal-continue-survey"
            type="button"
            variant="secondary"
            className="flex-1 justify-center text-center whitespace-nowrap"
            onClick={() => setIsOpen(false)}
          >
            <span>Continue Survey</span>
          </ButtonWithTracking>
        </div>
      </div>
    </dialog>
  );
}
